<template>
  <button @click.prevent="onClick" :disabled="isRedirecting">
    {{ isRedirecting ? "Leaving Site..." : "Go to Student Lending Center" }}
  </button>
  <teleport to="body">
    <custom-dialogue-modal
      v-if="showDialogueModal === true"
      :options="dialogueModalOptions"
      :closeBtn="dialogueModalOptions.closeBtn"
      @close="closeDialogueModal"
    />
  </teleport>
</template>

<script setup lang="ts">
import useApplicationStatus from "@/composables/application-status/useApplicationStatus";
import CustomDialogueModal from "@/components/modals/CustomDialogueModal.vue";
import useCustomDialogueModal from "@/composables/useCustomDialogueModal";
import useApplicationData from "@/composables/useApplicationData";
import { resolveMergeFields } from "@/use/formatter";
import { ButtonTypes } from "@/models/modal";

const props = defineProps<{
  openInNewTab: boolean;
}>();

const emit = defineEmits<{
  (e: "redirect", value: boolean): void;
}>();

const { isRedirecting, redirectToSLC } = useApplicationStatus();

const { emailAddress, hrefTFN, tollFreeNumber } = useApplicationData();

const { dialogueModalOptions, showDialogueModal, openDialogueModal, closeDialogueModal } =
  useCustomDialogueModal();

function onClick() {
  if (isRedirecting.value) return;

  try {
    redirectToSLC({ openInNewTab: false });
    isRedirecting.value = false;
  } catch {
    onError();
  }
}

function onError() {
  let error =
    "We couldn't take you to the Student Lending Center. Please refresh and try again. If the issue persists, please contact our support team at {{phoneNumber}} or {{email}}.";

  error = resolveMergeFields(error, {
    phoneNumber: `<a class="underline link" :href="\`tel:${hrefTFN.value}\`">${tollFreeNumber.value}</a>`,
    email: `<a class="underline link" :href="\`mailto:${emailAddress}\`">${emailAddress.value}</a>`,
  });

  showDialogueModal.value = false;

  dialogueModalOptions.value = {
    description: error,
    closeBtn: true,
    buttons: [
      {
        label: "Ok",
        type: Number(ButtonTypes.Confirm),
        onClick: async () => {
          closeDialogueModal();
          isRedirecting.value = false;
        },
      },
    ],
  };

  openDialogueModal(dialogueModalOptions.value);
}

defineExpose({
  redirectToSLC,
  onClick,
});
</script>

<style scoped>
button {
  display: flex;
  width: 100%;
  height: 34px;
  padding: 11px 16px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 4px;
  background: var(--theme-color);
  border-color: var(--theme-color);
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
}

@media (max-width: 992px) {
  button {
    max-width: 100%;
  }
}
</style>
